import type { z } from 'zod';

import type { MigrationContext } from '@sites/entity-migrator';

import { HORIZONTAL_ALIGN_BY_POSITION, VERTICAL_ALIGN_BY_POSITION } from '../../../mixins/alignment';
import { legacyDeviceNumber, legacyDeviceValue } from '../../../legacy-device-value';
import type { Device } from '../../../utils';
import { cssKeywordOrNull } from '../../../utils';
import { compactColumnProps } from '../../compact';

import {
    DEFAULT_FILTER_OVERLAY,
    DEFAULT_OVERLAY_COLOR,
    DEFAULT_OVERLAY_OPACITY,
    DEFAULT_SCROLL_EFFECT,
} from '../../constants';

import type { columnDefaultV0Schema } from '../default/v0';
import type { ColumnSpecificV0 } from '../specific/v0';
import type { ColumnZeroV0 } from '../zero/v0';
import type { ColumnDeviceStylesLegacy, ColumnStylesLegacy } from './data';
import type { LegacyColumn } from './entity';

/**
 * The *input* side of the schema, not the parsed one: `animation` is a
 * self-versioned feature with a default, so a column that never had one hands
 * the schema `undefined` and gets the seed back.
 */
type ColumnDefaultV0Input = z.input<typeof columnDefaultV0Schema>;

/**
 * Legacy style values are `string | number` — the host writes paddings as
 * numbers but nothing stopped a `'30'` getting in, so parse rather than
 * type-check.
 */
const num = (value: unknown): number => {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : 0;
};

const str = (value: unknown): string => (value == null ? '' : String(value));

/**
 * A field the editor used to acquire by merging a freshly seeded column over the
 * persisted one before validating. That merge is gone, so the default is applied
 * here instead — see `column/constants`.
 */
const seeded = <T>(value: T | null | undefined, fallback: T): T => value ?? fallback;

/**
 * A per-side/corner value falls back to the shorthand and `0` is a real value —
 * the same rule `borderRadiusCornerVar` / `borderSideVar` apply at render time.
 * Resolving it here is what lets v0 drop the shorthand fields entirely.
 */
const withFallback = (value: unknown, fallback: unknown): unknown => (value || value === 0 ? value : fallback);

type StylesLike = Partial<ColumnStylesLegacy & ColumnDeviceStylesLegacy> | null | undefined;
type DataLike = LegacyColumn['data'] | LegacyColumn['mobileData'] | null | undefined;

type DeviceBags = {
    styles: StylesLike;
    data: DataLike;
    /**
     * The host wrote device bags lazily, and the renderer read an absent one as
     * "inherit desktop" — it simply emitted no `-mobile` variables and let the
     * cascade do the rest. v0 has no cascade, so that inheritance is resolved
     * here instead.
     *
     * Styles and data are tracked separately because the host wrote them
     * separately: a column can have a `mobileData` background image and no
     * `mobileStyles` at all. Deciding the data bag from `hasStyles` would read
     * the desktop data there and drop the mobile image, tint and filter.
     */
    hasStyles: boolean;
    hasData: boolean;
};

const deviceBags = (column: LegacyColumn) => {
    const mobileStyles = column.mobileStyles;
    const mobileData = column.mobileData;
    // The tablet bags fall back to the mobile ones, as they always have.
    const tabletStyles = column.tabletStyles ?? mobileStyles;
    const tabletData = column.tabletData ?? mobileData;

    return {
        desktop: { styles: column.styles, data: column.data, hasStyles: true, hasData: true },
        tablet: {
            styles: tabletStyles,
            data: tabletData,
            hasStyles: Boolean(tabletStyles),
            hasData: Boolean(tabletData),
        },
        mobile: {
            styles: mobileStyles,
            data: mobileData,
            hasStyles: Boolean(mobileStyles),
            hasData: Boolean(mobileData),
        },
    };
};

type Bags = ReturnType<typeof deviceBags>;

/**
 * Every responsive leaf is emitted for all three breakpoints, so the consumers
 * never have to guess whether an absent key means zero or "same as desktop".
 * `read` is handed the desktop bags too, since that is what an unwritten device
 * used to inherit.
 */
const devices = <T>(bags: Bags, read: (bag: DeviceBags, desktop: DeviceBags, device: Device) => T) => ({
    desktop: read(bags.desktop, bags.desktop, 'desktop'),
    tablet: read(bags.tablet, bags.desktop, 'tablet'),
    mobile: read(bags.mobile, bags.desktop, 'mobile'),
});

/** One style key on a device: its own bag, or desktop where the bag is missing or holds the key empty. */
const styleValue = <K extends keyof NonNullable<StylesLike>>(bag: DeviceBags, desktop: DeviceBags, key: K) =>
    bag.hasStyles ? legacyDeviceValue(bag.styles?.[key], desktop.styles?.[key]) : desktop.styles?.[key];

/** What a device with no data bag of its own reads its *data* from. */
const inheritedData = (bag: DeviceBags, desktop: DeviceBags): DataLike => (bag.hasData ? bag.data : desktop.data);

const CORNER_KEY = {
    topLeft: 'borderTopLeftRadius',
    topRight: 'borderTopRightRadius',
    bottomRight: 'borderBottomRightRadius',
    bottomLeft: 'borderBottomLeftRadius',
} as const;

const SIDE_KEYS = {
    top: ['borderTopWidth', 'borderTopStyle', 'borderTopColor'],
    right: ['borderRightWidth', 'borderRightStyle', 'borderRightColor'],
    bottom: ['borderBottomWidth', 'borderBottomStyle', 'borderBottomColor'],
    left: ['borderLeftWidth', 'borderLeftStyle', 'borderLeftColor'],
} as const;

type CornerValues = Record<keyof typeof CORNER_KEY, number>;

const NO_CORNERS: CornerValues = { topLeft: 0, topRight: 0, bottomRight: 0, bottomLeft: 0 };

/** A corner falls back to its bag's `borderRadius`, then — on a device — to the desktop corner. */
const cornerValues = (styles: StylesLike, inherited: CornerValues = NO_CORNERS): CornerValues => ({
    topLeft: legacyDeviceNumber(withFallback(styles?.[CORNER_KEY.topLeft], styles?.borderRadius), inherited.topLeft),
    topRight: legacyDeviceNumber(withFallback(styles?.[CORNER_KEY.topRight], styles?.borderRadius), inherited.topRight),
    bottomRight: legacyDeviceNumber(
        withFallback(styles?.[CORNER_KEY.bottomRight], styles?.borderRadius),
        inherited.bottomRight
    ),
    bottomLeft: legacyDeviceNumber(
        withFallback(styles?.[CORNER_KEY.bottomLeft], styles?.borderRadius),
        inherited.bottomLeft
    ),
});

type SideValue = { width: number; style: string; color: string };

const NO_SIDE: SideValue = { width: 0, style: '', color: '' };

/**
 * The shorthand trio lives on the desktop bag only — the device bags inherit it
 * from there, which is why `shorthand` is threaded in rather than read off
 * `styles`. Only when the shorthand is empty too does a device fall through to
 * the desktop side — `inherited`, which only a device passes.
 *
 * A device read its side width truthily, so a stored `0` fell through to the
 * shorthand just like an empty key: a side switched off was still drawn there,
 * transparent and as wide as the shorthand, and took room (#69719 bug 21).
 */
const sideValue = (
    styles: StylesLike,
    shorthand: LegacyColumn['styles'],
    which: keyof typeof SIDE_KEYS,
    inherited?: SideValue
): SideValue => {
    const [widthKey, styleKey, colorKey] = SIDE_KEYS[which];
    const fallback = inherited ?? NO_SIDE;
    const width = inherited
        ? styles?.[widthKey] || shorthand?.borderWidth
        : withFallback(styles?.[widthKey], shorthand?.borderWidth);

    return {
        width: legacyDeviceNumber(width, fallback.width),
        style: str(legacyDeviceValue(withFallback(styles?.[styleKey], shorthand?.borderStyle), fallback.style)),
        color: str(legacyDeviceValue(withFallback(styles?.[colorKey], shorthand?.borderColor), fallback.color)),
    };
};

type SpacingValues = { top: number; right: number; bottom: number; left: number };

const NO_SPACING: SpacingValues = { top: 0, right: 0, bottom: 0, left: 0 };

const paddingValues = (styles: StylesLike, inherited: SpacingValues = NO_SPACING): SpacingValues => ({
    top: legacyDeviceNumber(styles?.paddingTop, inherited.top),
    right: legacyDeviceNumber(styles?.paddingRight, inherited.right),
    bottom: legacyDeviceNumber(styles?.paddingBottom, inherited.bottom),
    left: legacyDeviceNumber(styles?.paddingLeft, inherited.left),
});

/**
 * The legacy bag keeps a theme preset name and a literal colour in the same
 * string, and so does v0 (`mixins/color-value`) — the host's `colorCss` tells
 * them apart against its own palette, which this package cannot see. So the
 * string travels verbatim.
 */
const color = (value: unknown): string | null => (value == null ? null : String(value));

const envelope = (column: LegacyColumn) => ({
    id: column.id,
    parentId: column.sectionId,
    entityType: 'column' as const,
    children: column.widgets,
    version: 0 as const,
    // Created before the field existed — when is not known.
    meta: { createdAt: null },
});

/**
 * Legacy stored the same width twice — twelfths in `columnWidth`, percent in
 * `columnWidthPer` — and only one of them was ever read. Which one is the row's
 * to say, so the adapter passes its mode down and the unit is decided here, once.
 */
const resolveWidth = (data: LegacyColumn['data'], ctx?: MigrationContext) =>
    ctx?.parentIsFlexibleWidths
        ? { value: num(seeded(data?.columnWidthPer, 100)), unit: '%' as const }
        : { value: num(data?.columnWidth), unit: 'col' as const };

export const migrateLegacyDefaultToV0 = (input: LegacyColumn, ctx?: MigrationContext): ColumnDefaultV0Input => {
    const { data, styles } = input;
    const bags = deviceBags(input);

    /**
     * With styles synchronised the renderer made every device contour follow the
     * desktop flag, whatever the device bag said. v0 keeps `syncWithDesktop`
     * as an editor behaviour only — writes propagate — so the render-time
     * short-circuit is resolved into the stored value here.
     */
    const isBorderOn = (bag: DeviceBags, desktop: DeviceBags, device: Device): boolean => {
        if (data?.isSynchronizeStyle) {
            return Boolean(desktop.data?.isBorder);
        }

        // Tablet read `tabletData.isBorder ?? mobileData.isBorder`, so a tablet bag
        // saved with the flag empty takes the mobile one.
        return Boolean(device === 'tablet' ? (bag.data?.isBorder ?? input.mobileData?.isBorder) : bag.data?.isBorder);
    };

    /**
     * The same short-circuit, for the background overlay: the renderer recomputed
     * the device tint and filter only inside `if (!data.isSynchronizeStyle)`, so
     * under synchronised styles the desktop overlay cascaded down. Reading the
     * device bag here instead picks up the `false` it was seeded with and the tint
     * and the blur vanish below 992px.
     */
    const overlayData = (bag: DeviceBags, desktop: DeviceBags): DataLike =>
        data?.isSynchronizeStyle ? desktop.data : inheritedData(bag, desktop);

    /**
     * A device with no style bag kept the desktop paddings but narrowed the
     * sides — the bare 15px rule the JSS shipped.
     */
    const desktopPadding = paddingValues(styles);
    const devicePadding = (bag: DeviceBags, _desktop: DeviceBags, device: Device) => {
        if (device === 'desktop') {
            return { value: desktopPadding, unit: 'px' as const };
        }

        const value = bag.hasStyles
            ? paddingValues(bag.styles, desktopPadding)
            : { ...desktopPadding, left: 15, right: 15 };

        return { value, unit: 'px' as const };
    };

    const desktopCorners = cornerValues(styles);
    const deviceCorners = (bag: DeviceBags, _desktop: DeviceBags, device: Device) => ({
        value: device !== 'desktop' && bag.hasStyles ? cornerValues(bag.styles, desktopCorners) : desktopCorners,
        unit: 'px' as const,
    });

    const deviceSide = (which: keyof typeof SIDE_KEYS) => {
        const desktopSide = sideValue(styles, styles, which);

        return devices(bags, (bag, _desktop, device) =>
            device === 'desktop' ? desktopSide : sideValue(bag.styles, styles, which, desktopSide)
        );
    };

    return {
        ...envelope(input),
        entitySubType: 'default',
        props: compactColumnProps({
            width: resolveWidth(data, ctx),
            syncWithDesktop: data?.isSynchronizeStyle ?? true,

            verticalAlign: data?.verticalAlign ? VERTICAL_ALIGN_BY_POSITION[data.verticalAlign] : null,
            horizontalAlign: data?.horizontalAlign ? HORIZONTAL_ALIGN_BY_POSITION[data.horizontalAlign] : null,

            padding: devices(bags, devicePadding),
            // `isIndependentCorners` is dropped the same way `isIndependentBorders`
            // is below: the editor flattened the four corners onto the top-left
            // whenever it was off, so they say it themselves.
            borderRadius: {
                corners: devices(bags, deviceCorners),
            },
            // `isIndependentBorders` is dropped rather than carried: the editor
            // wrote all four sides together whenever it was off, so four equal
            // sides say the same thing and `isBorderIndependent` reads it back.
            border: {
                isEnabled: devices(bags, isBorderOn),
                // The shorthand trio lives on the desktop bag only, so it is what
                // every device side falls back to — never the desktop's own sides.
                top: deviceSide('top'),
                right: deviceSide('right'),
                bottom: deviceSide('bottom'),
                left: deviceSide('left'),
            },
            background: {
                color: devices(bags, (bag, desktop) => color(styleValue(bag, desktop, 'backgroundColor'))),
                image: devices(bags, (bag, desktop) => inheritedData(bag, desktop)?.backgroundImage ?? null),
                size: devices(bags, (bag, desktop) => cssKeywordOrNull(styleValue(bag, desktop, 'backgroundSize'))),
                position: devices(bags, (bag, desktop) =>
                    cssKeywordOrNull(styleValue(bag, desktop, 'backgroundPosition'))
                ),
                repeat: devices(bags, (bag, desktop) => cssKeywordOrNull(styleValue(bag, desktop, 'backgroundRepeat'))),
                overlay: {
                    isEnabled: devices(bags, (bag, desktop) => Boolean(overlayData(bag, desktop)?.isColorOverflow)),
                    color: devices(bags, (bag, desktop) =>
                        seeded(overlayData(bag, desktop)?.colorOverflow, DEFAULT_OVERLAY_COLOR)
                    ),
                    opacity: devices(bags, (bag, desktop) =>
                        num(seeded(overlayData(bag, desktop)?.colorOverflowOpacity, DEFAULT_OVERLAY_OPACITY))
                    ),
                },
                filter: {
                    isEnabled: devices(bags, (bag, desktop) => Boolean(overlayData(bag, desktop)?.isFilterOverlay)),
                    value: devices(bags, (bag, desktop) =>
                        seeded(overlayData(bag, desktop)?.filterOverlay, DEFAULT_FILTER_OVERLAY)
                    ),
                },
            },

            shadow: {
                isEnabled: Boolean(data?.isShadow),
                settings: data?.shadowSettings ?? null,
            },
            scrollEffect: {
                isEnabled: Boolean(data?.isScrollEffect),
                value: seeded(data?.scrollEffect, DEFAULT_SCROLL_EFFECT),
            },
            // The animation feature migrates itself: a bag without a `version`
            // is its own v0, and an absent one falls back to the seed.
            animation: data?.animation ?? undefined,
        }),
    };
};

/**
 * Header columns keep two knobs; the rest of the legacy bags are dropped because
 * nothing ever read them — see the note on `columnSpecificDataSchema`.
 */
export const migrateLegacySpecificToV0 = (input: LegacyColumn): ColumnSpecificV0 => ({
    ...envelope(input),
    entitySubType: 'specific',
    props: {
        // A header row has no flexible-width mode, so the unit is never in doubt.
        width: { value: num(input.data?.columnWidth), unit: 'col' },
        horizontalAlign: input.data?.horizontalAlign ? HORIZONTAL_ALIGN_BY_POSITION[input.data.horizontalAlign] : null,
    },
});

export const migrateLegacyZeroToV0 = (input: LegacyColumn): ColumnZeroV0 => ({
    ...envelope(input),
    entitySubType: 'zero',
    props: {},
});

/**
 * Columns are indistinguishable from one another in the persisted JSON — unlike
 * sections, not even the header ones carry a `type` tag. The row they sit in is
 * the only signal, so the adapter passes its subtype down and the registry
 * routes on it.
 */
export const columnVariantFromContext = (ctx?: MigrationContext): 'default' | 'specific' | 'zero' => {
    if (ctx?.parentType === 'specific') return 'specific';
    if (ctx?.parentType === 'zero') return 'zero';

    return 'default';
};
